import type { ClassifiedRouteWithStops, DriverCurrentRoutes, ResolvedRouteStop } from "@storyboard/web-client";
import type { DriverRouteSummary, RouteLifecycleSummary, RouteReference, RouteStopSummary } from "@/types/driverRoute";

/**
 * Stop states that mean the driver is done with the stop, whether or not they served it.
 *
 * Mirrors `RouteLifecycleClassifier.ts` in `automated-dispatch-utils`, so the board and the bot agree on which stop is
 * next.
 */
const TERMINAL_STOP_STATES: ReadonlySet<ResolvedRouteStop["state"]> = new Set([
  "arrived",
  "departed",
  "skipped",
  "bypassed",
]);

/**
 * Orders stops the way the driver runs them: by sequence, a stop without one last, ties by scheduled arrival then id.
 */
function byRunOrder(a: ResolvedRouteStop, b: ResolvedRouteStop): number {
  if (a.sequence !== b.sequence) {
    if (a.sequence === null) return 1;
    if (b.sequence === null) return -1;
    return a.sequence - b.sequence;
  }
  const scheduled = (a.scheduledArrivalTime ?? "").localeCompare(b.scheduledArrivalTime ?? "");
  return scheduled !== 0 ? scheduled : a.id.localeCompare(b.id);
}

function isTerminal(stop: ResolvedRouteStop): boolean {
  return TERMINAL_STOP_STATES.has(stop.state);
}

/**
 * The window a stop is due in: the vendor's own requested window where it states either bound, else the forecast's.
 *
 * The vendor's window wins because it is the stop's own and is the one the dispatch bot quotes, so the board and the
 * bot give a dispatcher the same time. The forecast's is only a fallback: Platform Science's reads one window for
 * every pickup and one for every delivery off the order's preplan, and no other source builds a forecast at all.
 */
function appointmentOf(stop: ResolvedRouteStop): RouteStopSummary["appointment"] {
  const { requestedArrivalWindowStart: start, requestedArrivalWindowEnd: end } = stop;
  if (start !== null || end !== null) {
    return { start, end };
  }

  // The generated client types the forecast and its appointment as present; the wire sends null where none exists.
  const forecasted = stop.forecast?.appointment ?? null;
  return forecasted ? { start: forecasted.start, end: forecasted.end } : null;
}

function summarizeStop(stop: ResolvedRouteStop): RouteStopSummary {
  // The generated client types the forecast as present; the wire sends null where none exists.
  const forecast = stop.forecast ?? null;
  // The generated client types this as present too; the wire sends null where the vendor states only one line.
  const postalAddress = stop.location.postalAddress ?? null;
  return {
    id: stop.id,
    name: stop.location.name,
    address: stop.location.address,
    city: postalAddress?.city ?? null,
    region: postalAddress?.region ?? null,
    latitude: stop.location.latitude,
    longitude: stop.location.longitude,
    role: stop.role,
    handling: stop.handling,
    state: stop.state,
    done: isTerminal(stop),
    eta: forecast?.arrival ?? stop.eta ?? stop.scheduledArrivalTime,
    arrivedAt: stop.actualArrivalTime,
    departedAt: stop.actualDepartureTime,
    appointment: appointmentOf(stop),
    timeZone: stop.location.timeZone,
    risk: forecast?.risk ?? null,
    legMiles: forecast?.legMiles ?? null,
    legKind: forecast?.legKind ?? null,
    arrivalObserved: forecast?.arrivalObserved ?? false,
    inForecast: forecast !== null,
  };
}

/** The vendor whose routes are named by the order number a dispatcher knows the load by. */
const ORDER_NUMBER_VENDOR: ClassifiedRouteWithStops["route"]["vendor"] = "platform_science";

/** A route reference's kind as the wire states it. */
type RouteReferenceKind = ClassifiedRouteWithStops["route"]["references"][number]["kind"];

/**
 * The label a Dispatch row shows each kind of reference under.
 *
 * Total rather than partial, so a kind added to the wire cannot compile until it has a label here.
 */
const REFERENCE_LABELS: Record<RouteReferenceKind, RouteReference["label"]> = {
  purchaseOrder: "PO",
  billOfLading: "BOL",
  trailer: "Trailer",
  pickupReference: "Pickup ref",
  deliveryReference: "Delivery ref",
  relayCode: "Relay code",
};

/**
 * The route's reference numbers as a Dispatch row labels them, in the vendor's order.
 *
 * Read off the route's typed `references` for every vendor alike, so nothing on the web parses the notes or asks which
 * vendor wrote them.
 */
function referencesOf(route: ClassifiedRouteWithStops["route"]): RouteReference[] {
  return route.references.map(({ kind, value }) => ({ label: REFERENCE_LABELS[kind], value }));
}

/**
 * The lifecycles a row will show, most wanted first: what the driver is on, else what they are queued for, else what
 * they just finished. The read groups its answer in this same order, so the first route of the first present group is
 * also the first entry the dispatch bot would answer from.
 */
const ROW_LIFECYCLES: readonly RouteLifecycleSummary[] = ["current", "nearFuture", "recentlyCompleted"];

/**
 * Folds a driver's near-term routes into the one summary a Dispatch row carries, or null for a driver on nothing.
 *
 * Chosen by lifecycle rather than position, as the read's contract asks: a running route wins, then a queued one, then
 * one finished inside the horizon, taking the read's own first entry within that group. A route the read classes
 * outside those three (distant past or future, indeterminate) never makes a row. Stops come out in run order, and the
 * next stop is the first of them the driver is not yet done with. A route with two or more driver assignments is a
 * team's: the source lists every driver on the job, primary then secondary.
 *
 * The forecast's rests keep neither their placement nor the vendor step they sit at: tying a rest to a stop would read
 * the stop's vendor key, which the API leaves unmodelled.
 */
export function summarizeDriverRoutes(routes: readonly ClassifiedRouteWithStops[]): DriverRouteSummary | null {
  const current = ROW_LIFECYCLES.map((lifecycle) => routes.find((route) => route.lifecycle === lifecycle)).find(
    (route) => route !== undefined,
  );
  if (!current) return null;
  const { route, stops, unresolvedStopCount } = current;
  const forecast = route.forecast ?? null;
  const ordered = [...stops].sort(byRunOrder).map(summarizeStop);
  return {
    routeId: route.id,
    lifecycle: current.lifecycle as RouteLifecycleSummary,
    vendorRouteId: route.vendorRouteId,
    name: route.name,
    nameIsOrderNumber: route.vendor === ORDER_NUMBER_VENDOR,
    notes: route.notes,
    references: referencesOf(route),
    startedAt: route.actualStartTime,
    scheduledStartAt: route.scheduledStartTime,
    scheduledEndAt: route.scheduledEndTime,
    stops: ordered,
    stopsDone: ordered.filter((stop) => stop.done).length,
    stopsTotal: ordered.length,
    unresolvedStopCount,
    isTeam: route.driverAssignments.length >= 2,
    nextStop: ordered.find((stop) => !stop.done) ?? null,
    pta: forecast?.plannedTimeOfAvailability ?? null,
    ptaIsActual: forecast?.isTerminal ?? false,
    forecastFlags: forecast ? [...forecast.flags] : [],
    forecastAsOf: forecast?.asOf ?? null,
    rests: (forecast?.rests ?? []).map(({ type, startsAt, endsAt, inProgress }) => ({
      type,
      startsAt,
      endsAt,
      inProgress,
    })),
    hosInForecast: forecast?.clocksAtPta != null,
    loadedMiles: forecast?.loadedMiles ?? null,
    emptyMiles: forecast?.emptyMiles ?? null,
  };
}

/**
 * The workspace read as the rows join it: each driver's summary by membership id. A driver the read omits (on no
 * route) has no entry.
 */
export function summarizeWorkspaceRoutes(
  drivers: readonly DriverCurrentRoutes[],
): ReadonlyMap<string, DriverRouteSummary> {
  const byMembership = new Map<string, DriverRouteSummary>();
  for (const driver of drivers) {
    const summary = summarizeDriverRoutes(driver.routes);
    if (summary) byMembership.set(driver.workspaceMembershipId, summary);
  }
  return byMembership;
}
